/**
 * Integração TiFlux (v12) — sincronização espelho TiFlux → ERP. Config
 * singleton por tenant (GET/PUT /tiflux/integration) + disparo manual
 * (POST /tiflux/sync-now, 202/409). Contratos do @erp/shared (tiflux.ts):
 * TifluxIntegration inclui `syncRunning` (rodada em voo neste processo),
 * que alimenta o "Sincronizando…"/poll da UI. `configured` é derivado:
 * tokenMasked != null (mesmo padrão do Asaas).
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TIFLUX_DEFAULT_ENDPOINT,
  TIFLUX_ENTITY_LABELS,
  TIFLUX_ERROR_LABELS,
  TIFLUX_PULL_FIELD_LABELS,
  type TifluxErpClient,
  type TifluxErpClientPreview,
  type TifluxIntegration,
  type TifluxIntegrationPutBody,
  type TifluxPullApplyBody,
  type TifluxPullApplyResult,
  type TifluxPullField,
  type TifluxPullPreview,
  type TifluxSyncError,
  type TifluxSyncNowResponse,
  type TifluxSyncResult,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

export {
  TIFLUX_DEFAULT_ENDPOINT,
  TIFLUX_ENTITY_LABELS,
  type TifluxErpClient,
  type TifluxErpClientPreview,
  type TifluxIntegration,
  type TifluxIntegrationPutBody,
  type TifluxSyncError,
  type TifluxSyncResult,
};

/** Opções do select de intervalo (o PUT aceita 5..1440 — a UI oferece 3). */
export const TIFLUX_INTERVAL_OPTIONS = [15, 30, 60] as const;

/** spec 026: o ciclo dos clientes operados no ERP (padrão 10; o PUT aceita 5..1440) */
export const TIFLUX_OPERATED_INTERVAL_OPTIONS = [5, 10, 15, 30, 60] as const;

export const TIFLUX_KEY = ['erp', 'tiflux', 'integration'] as const;

export function isTifluxConfigured(view: TifluxIntegration): boolean {
  return view.tokenMasked != null;
}

// ── Queries / mutations ─────────────────────────────────────────────────────

export function tifluxIntegrationOptions() {
  return queryOptions({
    queryKey: TIFLUX_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<TifluxIntegration>('/tiflux/integration', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function useUpdateTifluxIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TifluxIntegrationPutBody) => {
      const res = await api<TifluxIntegration>('/tiflux/integration', {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(TIFLUX_KEY, data);
      void qc.invalidateQueries({ queryKey: TIFLUX_KEY });
    },
  });
}

// ── Clientes operados no ERP (spec 024, FR-003) ─────────────────────────────

export const TIFLUX_ERP_CLIENTS_KEY = ['erp', 'tiflux', 'erp-clients'] as const;

/** GET /tiflux/erp-clients — o registro de cada desligamento (quem, quando, quantos) */
export function tifluxErpClientsOptions() {
  return queryOptions({
    queryKey: TIFLUX_ERP_CLIENTS_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<TifluxErpClient[]>('/tiflux/erp-clients', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** GET /tiflux/erp-clients/preview — o que o diálogo mostra antes de confirmar */
export function tifluxErpClientPreviewOptions(clientId: number) {
  return queryOptions({
    queryKey: [...TIFLUX_ERP_CLIENTS_KEY, 'preview', clientId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TifluxErpClientPreview>('/tiflux/erp-clients/preview', {
        query: { client_id: clientId },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * POST /tiflux/erp-clients — desliga o cliente do espelho. Todos os tickets
 * atuais dele saem do espelho: tudo que mostra ticket precisa reler.
 */
export function useDetachTifluxErpClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: number) => {
      const res = await api<TifluxErpClient>('/tiflux/erp-clients', {
        method: 'POST',
        body: { clientId },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TIFLUX_ERP_CLIENTS_KEY });
      void qc.invalidateQueries({ queryKey: ['erp', 'clients'] });
      void qc.invalidateQueries({ queryKey: ['erp', 'tickets'] });
    },
  });
}

// ── "Puxar do TiFlux" (spec 026, FR-004) ────────────────────────────────────

export const TIFLUX_PULL_KEY = ['erp', 'tiflux', 'pull'] as const;

/**
 * GET /tiflux/erp-clients/:clientId/pull — a prévia, buscada no TiFlux NA HORA
 * (segundos: listas do cliente e, para quem mudou lá, comentários e
 * apontamentos). Sem cache: cada abertura do diálogo consulta de novo.
 */
export function tifluxPullPreviewOptions(clientId: number) {
  return queryOptions({
    queryKey: [...TIFLUX_PULL_KEY, clientId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TifluxPullPreview>(`/tiflux/erp-clients/${clientId}/pull`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** POST /tiflux/erp-clients/:clientId/pull — aplica; muda tickets, pendências e contagens */
export function useApplyTifluxPull(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TifluxPullApplyBody) => {
      const res = await api<TifluxPullApplyResult>(`/tiflux/erp-clients/${clientId}/pull`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['erp', 'clients'] });
      void qc.invalidateQueries({ queryKey: ['erp', 'tickets'] });
    },
  });
}

export function tifluxPullFieldLabel(field: TifluxPullField): string {
  return TIFLUX_PULL_FIELD_LABELS[field];
}

/** por que um campo não entra (prévia) ou não entrou (aplicar) — pt-BR */
const PULL_REASON_LABELS: Record<string, string> = {
  stage_not_linked: 'a etapa do TiFlux ainda não tem vínculo no ERP (a próxima rodada do espelho cria)',
  desk_not_linked: 'a mesa do TiFlux ainda não tem vínculo no ERP',
  responsible_not_linked: 'o responsável do TiFlux não tem vínculo no ERP',
  stage_missing: 'o TiFlux não informou a etapa',
  desk_missing: 'o TiFlux não informou a mesa',
  ticket_closed: 'fechado no ERP e no TiFlux — reabra no ERP para mudar',
  stage_not_in_desk: 'a etapa do TiFlux é de outra mesa',
  ticket_not_open: 'o ticket está fechado no ERP',
  ticket_not_operated: 'o ticket não é mais deste cliente operado',
  tiflux_ticket_missing: 'o ticket não existe mais no TiFlux',
  pull_budget_exceeded: 'o TiFlux demorou a responder — tente de novo',
  not_applicable: 'não dá para aplicar agora',
};

export function tifluxPullReason(code: string | null): string {
  if (!code) return '';
  if (code.startsWith('requires_')) {
    const fields = code
      .slice('requires_'.length)
      .split('_')
      .filter((f): f is TifluxPullField => f in TIFLUX_PULL_FIELD_LABELS)
      .map((f) => TIFLUX_PULL_FIELD_LABELS[f].toLowerCase());
    return `só junto com ${fields.join(' e ')}`;
  }
  if (code.startsWith('detail_failed')) return 'o TiFlux não respondeu por este ticket';
  return PULL_REASON_LABELS[code] ?? code;
}

/** avisos da prévia (lista parcial, filtro ignorado, ticket sumido) — pt-BR */
export function tifluxPullWarning(code: string): string {
  if (code === 'client_filter_ignored')
    return 'O TiFlux ignorou o filtro por cliente: a consulta leu as listas completas.';
  if (code === 'tiflux_list_partial') return 'Parte das listas do TiFlux não respondeu — a prévia pode estar incompleta.';
  const [kind, n] = code.split(':');
  if (kind === 'tiflux_ticket_missing') return `O ticket #${n} não existe mais no TiFlux.`;
  if (kind === 'detail_failed') return `O TiFlux não respondeu pelo ticket #${n}.`;
  return code;
}

/** POST /tiflux/sync-now — 202 {started:true}; 409 sync_already_running. */
export function useTifluxSyncNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api<TifluxSyncNowResponse>('/tiflux/sync-now', { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    // Refetch imediato para o GET refletir syncRunning=true.
    onSuccess: () => void qc.invalidateQueries({ queryKey: TIFLUX_KEY }),
  });
}

// ── Formatação ──────────────────────────────────────────────────────────────

/** "agora" / "há N min" / "há N h" / "ontem" / "há N dias" / dd/mm/aaaa. */
export function tifluxRelativeTime(value: string | Date): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const minutes = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  return d.toLocaleDateString('pt-BR');
}

// ── Erros pt-BR ─────────────────────────────────────────────────────────────

/** Códigos locais extras aos do shared (wording transversal do web). */
const EXTRA_ERROR_LABELS: Record<string, string> = {
  forbidden: 'Apenas administradores podem alterar a integração do TiFlux.',
};

/** Shared TIFLUX_ERROR_LABELS primeiro; os transversais (permission_denied…)
 *  caem no fallback genérico do ticket-format — fonte única do wording. */
export function tifluxErrorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const code = (err as { error: string }).error;
    const label = TIFLUX_ERROR_LABELS[code] ?? EXTRA_ERROR_LABELS[code];
    if (label) return label;
  }
  return genericErrorMessage(err);
}
